import { AuthenticatorKey, IdxStep } from '@mgmresorts/mgm-auth-js-sdk';
import { TextInput } from '@mgmresorts/mgm-ui';
import { navigate } from 'hookrouter';
import qs from 'query-string';
import React, { useCallback, useMemo, useState, useContext, useEffect, useRef, Suspense } from 'react';
import { ShellAppHeaderProps } from 'web_mfe_shell/ShellAppHeader';

import { KNOWN_ERROR_MESSAGES } from '../../api/constants';
import { checkUser } from '../../api/fetch/signInOrJoin';
import { useClientIntegrationContent } from '../../common/api';
import { Alert } from '../../components/ui/Alert';
import { Container } from '../../components/ui/Container';
import { Logo } from '../../components/ui/Logo';
import { MGMRewardsPromoBanner } from '../../components/ui/MGMRewardsPromoBanner';
import { PageLayout } from '../../components/ui/PageLayout';
import { VerifyIdentityModal } from '../../components/ui/VerifyIdentityModal';
import { useAroIdMeEnabled, useMfaEnabled, usePasskeyEnabled, useStepUpChallenge } from '../../hooks';
import { useContent } from '../../hooks/useContent';
import { useDerivedPropertySlug } from '../../hooks/useDerivedPropertySlug';
import { useFieldValidation } from '../../hooks/useFieldValidation';
import { useInput } from '../../hooks/useInput';
import { useLoadingLabel } from '../../hooks/useLoadingLabel';
import { useMobile } from '../../hooks/useMediaQuery';
import { EventAttributes } from '../../utils/analytics/types';
import { useEvent } from '../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../utils/analytics/usePageLoadEvent';
import { useAuthContext } from '../../utils/auth/AuthProvider';
import { ClientId, IDENTITY_WIDGET_PATHS, MaxLengths } from '../../utils/constants';
import { getFeaturesByClientId } from '../../utils/getFeaturesByClientId';
import { canUsePasskey } from '../../utils/passkey';
import { PersistentParametersContext } from '../../utils/persistParameterContext';
import { UserAccountContext } from '../../utils/userAccountContext';
import { emailValidator } from '../../utils/validators';
import { StyledLegalText } from '../SignIn/styles';

import { StyledButton, StyledHeading, StyledSubTitle, StyledFooterContainer } from './styles';

const ShellGlobalFooter = React.lazy(() => import('web_mfe_shell/GlobalFooter'));
const ShellAppHeader = React.lazy(() => import('web_mfe_shell/ShellAppHeader'));

export function SignInOrJoin() {
  const isMobile = useMobile();
  const trackEvent = useEvent();
  usePageLoadEvent('identity', 'home');
  const [error, setError] = useState<null | string>(null);
  const [isLoading, setIsLoading] = useState(false);
  const startEmailEntry = useRef(false);
  const { validateField, invalidFields, emptyFields } = useFieldValidation([emailValidator]);
  const { getContentByPath } = useContent();
  const { setUserEmail, setPreferredFactor, setHasPasskeyEnrolled, mgmId } = useContext(UserAccountContext);
  const queryParams = qs.parse(window.location.search);
  const { params } = useContext(PersistentParametersContext);
  const { email, client_id, redirect_uri } = queryParams;
  const { isAllowOnlySignIn, isDisplayMGMRewardsPromoBanner } = getFeaturesByClientId(
    (client_id || params?.client_id) as ClientId,
  );
  const hasInternalRedirect = ['mgmresorts.com', 'devtest.vegas'].some(domain => document?.referrer.includes(domain));
  let emailId = '';
  if (email) {
    emailId = email as string;
  }
  const { authClient } = useAuthContext();
  const [analyticsFieldErrors, setAnalyticsFieldErrors] = useState<string[]>([]);

  const { bind: bindUsername, value: login } = useInput(
    emailId,
    'email',
    (field, value) => {
      validateField(field, value);

      if (field === 'email' && value?.length === 1 && !startEmailEntry.current) {
        startEmailEntry.current = true;
        trackEvent('identity – start email entry', 'start email entry', 'startemailentry', {}, true);
      }
    },
    validateField,
  );
  const pageTitle: string = isAllowOnlySignIn
    ? getContentByPath('signInOrJoin.secondaryTitle')
    : getContentByPath('signInOrJoin.title');
  const subTitle: string = getContentByPath('signInOrJoin.subTitle');

  const signInOrJoinCTA = useLoadingLabel(isLoading, 'signInOrJoin.next');

  /**
   * Disclaimer and third-party client error messages sourced
   * from CMS (`ui_identity`) with automatic fallback to bundled content.
   */
  const {
    disclaimer: legalText,
    authFailedError,
    lockedOutError,
  } = useClientIntegrationContent((client_id as string | undefined) ?? (params?.client_id as string | undefined));
  const property = useDerivedPropertySlug();

  // Check ENABLE_MFA and ENABLE_PASSKEY feature flags
  const { mfaEnabled } = useMfaEnabled();
  const { passkeyEnabled } = usePasskeyEnabled();
  const { aroIdMeEnabled } = useAroIdMeEnabled();
  const { stepupContextId, isStepUpModalOpen, handleStepUpChallenge, closeStepUpModal, useDifferentAccount } =
    useStepUpChallenge();

  useEffect(() => {
    if (invalidFields.length > 0 || emptyFields.length > 0) {
      const eventAttributes = {} as EventAttributes;
      if (
        (invalidFields.includes('email') || emptyFields.includes('email')) &&
        !analyticsFieldErrors.includes('email')
      ) {
        eventAttributes.errorField = 'Email';
        eventAttributes.errorDisplayed = getContentByPath('signIn.fields.email.error');
      }
    }

    invalidFields.length > 0 && setAnalyticsFieldErrors(invalidFields);
    emptyFields.length > 0 && setAnalyticsFieldErrors(emptyFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidFields, invalidFields.length, emptyFields, emptyFields.length]);

  // Memoized passkey enrollment check
  const checkPasskeyEnrollment = useMemo(() => {
    return async (email: string) => {
      let passkeyEnrolled = false;
      try {
        await authClient.idx.startAuthenticate(email);
        await authClient.idx.stepHandlers[IdxStep.Identify].Identify(email);
        const passKeyAuthHandler = authClient.idx.stepHandlers[IdxStep.AuthPasskey];
        passkeyEnrolled = passKeyAuthHandler.canSelectAuth(AuthenticatorKey.WEBAUTHN);
        console.log('passkeyenrolled', passkeyEnrolled);
      } catch (error) {
        passkeyEnrolled = false;
      }

      // Check if user can use passkey
      const passkeyCompatible = await canUsePasskey(authClient, mgmId);

      return passkeyEnrolled && passkeyCompatible;
    };
  }, [authClient, mgmId]);

  const processSignInOrJoin = useCallback(
    async username => {
      try {
        const accountTypePath = {
          'sign-in': 'signin',
          'sign-up': 'signup',
          activate: 'activate',
          reset_password: 'forgot-password',
        };
        const trackAccountType = {
          'sign-up': 'JOIN',
        };
        setIsLoading(true);

        // TODO: Check if MFA is enabled for the user and adjust flow (legacy vs mfa) accordingly
        const { email, accountType, preferredFactor } = await checkUser(username, aroIdMeEnabled);
        if (!accountType) throw Error(KNOWN_ERROR_MESSAGES['invalid_token']);
        const userAccountType: keyof typeof accountTypePath = accountType?.toLowerCase();
        setUserEmail(email);

        // Store preferredFactor if it exists
        if (preferredFactor) {
          setPreferredFactor(preferredFactor);
        }

        // Check passkey enrollment and compatibility
        const canUsePasskeyValue = passkeyEnabled && (await checkPasskeyEnrollment(email));
        setHasPasskeyEnrolled(canUsePasskeyValue);
        await trackEvent(
          'identity – next - path assignment click',
          'next',
          'nextpathclick',
          { pathAssigned: trackAccountType[userAccountType as keyof typeof trackAccountType] || accountType },
          true,
        );

        if (isAllowOnlySignIn && userAccountType !== 'sign-in') {
          // For reset_password, always prefer the locked-out message (hook guarantees a non-empty value).
          const errorMessage =
            userAccountType === 'reset_password' && lockedOutError ? lockedOutError : authFailedError;

          throw Error(errorMessage.replaceAll('{REDIRECT_URI}', redirect_uri as string));
        } else if (mfaEnabled && passkeyEnabled && canUsePasskeyValue && userAccountType === 'sign-in') {
          navigate(IDENTITY_WIDGET_PATHS.SignInAuthSelection, true);
        } else {
          navigate(`/${accountTypePath[userAccountType as keyof typeof accountTypePath]}`, true);
        }
      } catch (err) {
        if (handleStepUpChallenge(err)) return;
        if (err instanceof Error) {
          setError(err.message);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      setUserEmail,
      trackEvent,
      isAllowOnlySignIn,
      redirect_uri,
      mfaEnabled,
      passkeyEnabled,
      aroIdMeEnabled,
      handleStepUpChallenge,
      checkPasskeyEnrollment,
      setPreferredFactor,
      setHasPasskeyEnrolled,
      authFailedError,
      lockedOutError,
    ],
  );

  const canRequestSignInOrJoin = useMemo(
    () => !invalidFields.length && !emptyFields.length && login && !isLoading,
    [invalidFields.length, isLoading, login, emptyFields.length],
  );

  const renderAlerts = () => (
    <>
      {error && <Alert text={error} data-testid='sign-in-or-join-error' />}
      {queryParams.message && getContentByPath(`signIn.message.${queryParams.message}`) && (
        <Alert type='info' text={getContentByPath(`signIn.message.${queryParams.message}`)} />
      )}
      {!queryParams.message && params.partner && getContentByPath(`signIn.message.partner.${params.partner}`) && (
        <Alert type='info' text={getContentByPath(`signIn.message.partner.${params.partner}`)} />
      )}
    </>
  );

  const renderForm = () => (
    <div>
      <TextInput
        variant='default'
        label={getContentByPath('signIn.fields.email.title')}
        id='email'
        testId='email'
        disabled={isLoading || !!emailId}
        error={invalidFields.includes('email') || emptyFields.includes('email')}
        hint={
          emptyFields.includes('email')
            ? getContentByPath('signIn.fields.email.error')
            : invalidFields.includes('email')
            ? getContentByPath('signIn.fields.email.invalid')
            : ''
        }
        {...bindUsername}
        maxLength={MaxLengths.Email}
        type='email'
        autoComplete='email'
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            validateField('email', login);
            if (!invalidFields.length && !emptyFields.length) {
              processSignInOrJoin(login);
            }
          }
        }}
      />
      {!!legalText && (
        <StyledLegalText variant='body-regular-m' testId='legal-text'>
          {legalText}
        </StyledLegalText>
      )}
      <StyledButton
        $hasPromoBanner={isDisplayMGMRewardsPromoBanner}
        label={signInOrJoinCTA}
        onClick={e => {
          e?.preventDefault();
          processSignInOrJoin(login);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            processSignInOrJoin(login);
          }
        }}
        disabled={!canRequestSignInOrJoin}
        testId='sign-in-or-join'
        loading={isLoading}
        shape='square'
        size='large'
        variant='primary'
        fullWidth
        type='submit'
      />
    </div>
  );

  const renderSharedContent = () => (
    <>
      {(!isDisplayMGMRewardsPromoBanner || (isDisplayMGMRewardsPromoBanner && !isMobile)) && <Logo />}
      <StyledHeading
        $hasPromoBanner={isDisplayMGMRewardsPromoBanner}
        variant='heading-medium-3'
        as='h1'
        testId='page-title'
      >
        {pageTitle}
      </StyledHeading>

      {renderAlerts()}

      {isDisplayMGMRewardsPromoBanner && (
        <StyledSubTitle variant='body-regular-m' as='p' testId='sign-in-or-join-subtitle'>
          {subTitle}
        </StyledSubTitle>
      )}
      {renderForm()}
    </>
  );

  const desktopConfig = useMemo<ShellAppHeaderProps['desktopConfig']>(
    () => ({
      enableCartIcon: true,
      enableExploreResorts: false,
      enableGlowAnimation: false,
      enableSearch: false,
      enableSecondaryNav: false,
      enableSignIn: false,
      enableSiteLogo: true,
    }),
    [],
  );

  const mobileConfig = useMemo<ShellAppHeaderProps['mobileConfig']>(
    () => ({
      displayMobileBackButton: hasInternalRedirect,
      enableCartIcon: true,
      enableSecondaryNav: false,
      enableSidebarMenus: false,
      enableSignIn: false,
      enableSiteLogo: true,
      enableMenu: false,
      onMobileBackButtonClick: () => {
        window.history.back();
      },
    }),
    [hasInternalRedirect],
  );

  return (
    <>
      <div>
        {isDisplayMGMRewardsPromoBanner && (
          <Suspense fallback={<div>Loading...</div>}>
            <ShellAppHeader
              property={property}
              desktopConfig={desktopConfig}
              mobileConfig={mobileConfig}
              cartVariant={'icon'}
            />
          </Suspense>
        )}
        <PageLayout testId='route-/' title={pageTitle} description={getContentByPath('signInOrJoin.description')}>
          <Container
            height='auto'
            isDisplayMGMRewardsPromoBanner={isDisplayMGMRewardsPromoBanner}
            flexDirection={isDisplayMGMRewardsPromoBanner ? 'row' : 'column'}
            gap='40px'
          >
            <Container height='auto' flexDirection='column'>
              {renderSharedContent()}
            </Container>
            {isDisplayMGMRewardsPromoBanner && (
              <Container height='auto' flexDirection='column'>
                <MGMRewardsPromoBanner />
              </Container>
            )}
          </Container>
        </PageLayout>
        {isDisplayMGMRewardsPromoBanner && (
          <Suspense fallback={<div>Loading...</div>}>
            <StyledFooterContainer $isMobile={isMobile}>
              <ShellGlobalFooter property={property} footerType='text' />
            </StyledFooterContainer>
          </Suspense>
        )}
      </div>
      {aroIdMeEnabled && (
        <VerifyIdentityModal
          isOpen={isStepUpModalOpen}
          stepupContextId={stepupContextId ?? ''}
          onClose={closeStepUpModal}
          onUseDifferentAccount={useDifferentAccount}
        />
      )}
    </>
  );
}

SignInOrJoin.displayName = 'SignInOrJoin';
